/**
 * Turns what is on screen into an email document.
 *
 * Each builder reads the same records the screen renders from -- the analyst's
 * reply, a dashboard's panel results, a document's sections -- so the email
 * carries the figures the user is looking at and nothing the model wrote
 * outside them.
 */
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Markdown } from "../../components/Markdown";
import type { ArtifactComposedEvent } from "../../services/agui";
import {
  chronologicalRows,
  displayColumns,
  formatValue,
  memberLabel,
} from "../../services/chartOptions";
import type { PanelImage } from "../../services/chartRegistry";
import { readableText } from "../../services/displayText";
import {
  dashboardEndorsementLabel,
  dashboardPeriodNotes,
} from "../../services/governanceNotes";
import type { ArtifactSection, Dashboard, PanelResult } from "../../types";
import type { EmailBlock, EmailDocument, TextRun } from "./emailDocument";

/** Enough to read in a message body; the attachment carries the rest. */
export const MAX_EMAIL_TABLE_ROWS = 25;
const MAX_EMAIL_TABLE_COLUMNS = 8;
const GENERATED_BY = "Generated by DMI Agentic Analytics.";

export interface EmailContextLabel {
  contextId: string;
  contextVersion?: string | null;
  name?: string;
}

function contextLine(context: EmailContextLabel | null | undefined): string | null {
  if (!context?.contextId) return null;
  const name =
    context.name && context.name !== context.contextId
      ? `${context.name} (${context.contextId})`
      : context.contextId;
  return `Context ${name} · version ${context.contextVersion || "current"}`;
}

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------

function runsOf(node: Node): TextRun[] {
  const runs: TextRun[] = [];
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent) runs.push({ text: child.textContent });
    } else if (child instanceof Element) {
      if (child.tagName === "STRONG" || child.tagName === "B") {
        runs.push({ text: child.textContent ?? "", bold: true });
      } else {
        runs.push(...runsOf(child));
      }
    }
  });
  return runs;
}

function cellText(cell: Element): string {
  return (cell.textContent ?? "").trim();
}

function tableBlock(columns: string[], rows: string[][], total: number): EmailBlock {
  const shown = rows.slice(0, MAX_EMAIL_TABLE_ROWS);
  return {
    kind: "table",
    columns,
    rows: shown,
    note:
      total > shown.length
        ? `Showing ${shown.length} of ${total} rows.`
        : undefined,
  };
}

function blocksOf(root: Element): EmailBlock[] {
  const blocks: EmailBlock[] = [];
  for (const element of Array.from(root.children)) {
    switch (element.tagName) {
      case "P": {
        const runs = runsOf(element);
        if (!runs.some((run) => run.text.trim())) break;
        blocks.push(
          element.classList.contains("markdown-heading")
            ? { kind: "heading", text: cellText(element) }
            : { kind: "paragraph", runs },
        );
        break;
      }
      case "UL":
      case "OL":
        blocks.push({
          kind: "list",
          ordered: element.tagName === "OL",
          items: Array.from(element.querySelectorAll(":scope > li")).map(runsOf),
        });
        break;
      case "TABLE": {
        const columns = Array.from(element.querySelectorAll("thead th")).map(cellText);
        const rows = Array.from(element.querySelectorAll("tbody tr")).map((row) =>
          Array.from(row.querySelectorAll("td")).map(cellText),
        );
        blocks.push(tableBlock(columns, rows, rows.length));
        break;
      }
      default:
        if (element.children.length) {
          blocks.push(...blocksOf(element));
        } else if (cellText(element)) {
          blocks.push({ kind: "paragraph", runs: [{ text: cellText(element) }] });
        }
    }
  }
  return blocks;
}

/**
 * Parsed through the transcript's own renderer, so a table or a business
 * title in the email is exactly the one the user read in the conversation.
 */
export function markdownBlocks(text: string): EmailBlock[] {
  if (!text.trim()) return [];
  const markup = renderToStaticMarkup(createElement(Markdown, { text }));
  const parsed = new DOMParser().parseFromString(markup, "text/html");
  return blocksOf(parsed.body);
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

function unitsOf(result: PanelResult): Record<string, string> {
  return result.provenance?.units ?? result.units ?? {};
}

export function resultTable(result: PanelResult): EmailBlock | null {
  if (!result.columns.length || !result.rows.length) return null;
  const columns = displayColumns(result.columns).slice(0, MAX_EMAIL_TABLE_COLUMNS);
  const units = unitsOf(result);
  const rows = chronologicalRows(result.rows, result.columns).map((row) =>
    columns.map((column) => formatValue(row[column], column)),
  );
  return tableBlock(
    columns.map((column) => memberLabel(column, units)),
    rows,
    Math.max(result.rowCount, rows.length),
  );
}

function imageFor(
  images: PanelImage[],
  panelId: string,
  alt: string,
): EmailBlock | null {
  const image = images.find((item) => item.panelId === panelId);
  return image ? { kind: "image", id: panelId, dataUrl: image.dataUrl, alt } : null;
}

/** Stated before any figure, so a comparison is read with the caveat in mind. */
function periodNoteBlocks(results: Iterable<PanelResult | null>): EmailBlock[] {
  return dashboardPeriodNotes([...results].map((item) => item ?? undefined)).map(
    (note): EmailBlock => ({
      kind: "paragraph",
      runs: [{ text: "Incomplete period: ", bold: true }, { text: note }],
    }),
  );
}

function freshest(results: Iterable<PanelResult>): string | null {
  const stamps = [...results]
    .map((result) => result.provenance?.executedAt)
    .filter((value): value is string => Boolean(value))
    .sort();
  const newest = stamps.pop();
  if (!newest) return null;
  const date = new Date(newest);
  return Number.isNaN(date.getTime())
    ? null
    : `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/** One analyst reply, titled by the question it answered. */
export function answerEmailDocument(input: {
  answer: string;
  question?: string;
  context?: EmailContextLabel | null;
}): EmailDocument {
  const question = input.question?.trim() ?? "";
  const title =
    question.length > 120 ? `${question.slice(0, 117)}…` : question || "DMI analysis";
  const footer = [contextLine(input.context), GENERATED_BY].filter(
    (line): line is string => Boolean(line),
  );
  return { title, blocks: markdownBlocks(input.answer), footer };
}

export function dashboardEmailDocument(input: {
  dashboard: Dashboard;
  results: Map<string, PanelResult>;
  images: PanelImage[];
}): EmailDocument {
  const { dashboard, results, images } = input;
  const blocks: EmailBlock[] = [];
  if (dashboard.description.trim()) {
    blocks.push({ kind: "paragraph", runs: [{ text: dashboard.description.trim() }] });
  }
  blocks.push(...periodNoteBlocks(results.values()));

  const visible = dashboard.panels.filter((panel) => !panel.hidden);
  const omitted: string[] = [];
  for (const panel of visible) {
    const result = results.get(panel.id);
    if (!result || result.status !== "ok") {
      omitted.push(panel.title);
      continue;
    }
    blocks.push({ kind: "heading", text: panel.title });
    if (panel.narrative.trim()) {
      blocks.push({ kind: "paragraph", runs: [{ text: panel.narrative.trim() }] });
    }
    const image = imageFor(images, panel.id, panel.title);
    if (image) blocks.push(image);
    const table = resultTable(result);
    if (table) blocks.push(table);
  }
  if (omitted.length) {
    blocks.push({
      kind: "paragraph",
      runs: [
        { text: "Not included because their data was unavailable: ", bold: true },
        { text: omitted.join(", ") },
      ],
    });
  }

  const asOf = freshest(results.values());
  const endorsement = dashboardEndorsementLabel(results.values());
  const footer = [
    contextLine({
      contextId: dashboard.contextId,
      contextVersion: dashboard.contextVersion,
    }),
    asOf ? `Data as at ${asOf}` : null,
    endorsement ? `Semantic model: ${endorsement}` : null,
    GENERATED_BY,
  ].filter((line): line is string => Boolean(line));
  return { title: dashboard.name, blocks, footer };
}

/** The panel id a document section's chart registers under on screen. */
export function sectionPanelId(section: ArtifactSection, index: number): string {
  return ("panelId" in section && section.panelId) || `section-${index}`;
}

function previewResult(
  event: ArtifactComposedEvent,
  index: number,
): PanelResult | null {
  const preview = (event.spec.previews ?? []).find(
    (item) => item.sectionIndex === index,
  );
  if (!preview || preview.rows.length === 0) return null;
  return {
    panelId: `section-${index}`,
    status: "ok",
    columns: [...new Set(preview.rows.flatMap((row) => Object.keys(row)))],
    rows: preview.rows,
    rowCount: preview.rowCount,
    truncated: !preview.complete,
    units: preview.units,
    endorsement: preview.endorsement ?? null,
    periodNotes: preview.periodNotes ?? [],
    provenance: null,
    error: null,
  };
}

export function artifactEmailDocument(input: {
  event: ArtifactComposedEvent;
  published: boolean;
  images: PanelImage[];
}): EmailDocument {
  const { event, published, images } = input;
  const blocks: EmailBlock[] = [];
  const queried = event.spec.sections.map((_, index) => previewResult(event, index));
  blocks.push(...periodNoteBlocks(queried));
  event.spec.sections.forEach((section, index) => {
    blocks.push({ kind: "heading", text: readableText(section.title) });
    switch (section.kind) {
      case "headline":
        if (section.headline.trim()) {
          blocks.push({
            kind: "paragraph",
            runs: [{ text: readableText(section.headline.trim()), bold: true }],
          });
        }
        blocks.push(...markdownBlocks(section.body));
        break;
      case "kpi": {
        const captioned = section.tiles.some((tile) => tile.caption);
        blocks.push({
          kind: "table",
          columns: captioned ? ["Measure", "Value", "Basis"] : ["Measure", "Value"],
          rows: section.tiles.map((tile) => [
            readableText(tile.label),
            tile.value === null ? "Unavailable" : formatValue(tile.value, tile.metric),
            ...(captioned ? [readableText(tile.caption ?? "")] : []),
          ]),
        });
        break;
      }
      case "findings":
        blocks.push({
          kind: "list",
          ordered: false,
          items: section.items.map((item) => [
            { text: readableText(item.heading), bold: true },
            ...(item.detail ? [{ text: ` — ${readableText(item.detail)}` }] : []),
          ]),
        });
        break;
      default: {
        if (section.narrative.trim()) {
          blocks.push({
            kind: "paragraph",
            runs: [{ text: readableText(section.narrative.trim()) }],
          });
        }
        const image = imageFor(images, sectionPanelId(section, index), section.title);
        if (image) blocks.push(image);
        const result = queried[index];
        const table = result ? resultTable(result) : null;
        if (table) blocks.push(table);
        else if (!image) {
          blocks.push({ kind: "paragraph", runs: [{ text: "No in-scope data." }] });
        }
      }
    }
  });

  const endorsement = dashboardEndorsementLabel(
    queried.filter((item): item is PanelResult => item !== null),
  );
  const footer = [
    `Version ${event.version} · ${published ? "Published" : "Draft"}`,
    contextLine({
      contextId: event.spec.contextId,
      contextVersion: event.spec.contextVersion,
    }),
    endorsement ? `Semantic model: ${endorsement}` : null,
    GENERATED_BY,
  ].filter((line): line is string => Boolean(line));
  return { title: event.title, blocks, footer };
}
